import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MdLockPerson } from 'react-icons/md';
import CustomFormEmail from '../../CustomFormEmail/CustomFormEmail';
import CustomFormMobile from '../../CustomFormMobile/CustomFormMobile';
import { hasDollarInString } from '../../../../utils/utils';
import classes from './CfOtpVerification.module.scss';

const CfOtpVerification = (props) => {
    const {
        formBlockData,
        formik,
        studentDetails,
        isStudentVerified,
        studentDetailsApiCount,
        isOtpVerified,
        setIsOtpVerified,
        formBlock,
        maskedNumberString,
        maskedEmailString,
        duplicateMobileNumber,
        setDuplicateMobileNumber,
        customerNumber,
        // New props for primary identifier
        primaryIdentifier,
        isPrimaryIdentifierFlow,
        isPrimaryOtpSent,
        setIsPrimaryOtpSent,
        isPrimaryOtpVerified,
        setIsPrimaryOtpVerified,
        maskedPrimaryString,
        shouldShowPrimaryOtp,
        shouldShowCustomerOtpWithPrimary
    } = props;

    const mobileInput = formBlockData?.fields?.flat()?.find((field) => field?.input_id === 'customer_mobile');
    const emailInput = formBlockData?.fields?.flat()?.find((field) => field?.input_id === 'customer_email');

    const isActiveStudent = studentDetails?.stud_bd?.status === 'ACTIVE' || false;
    const blockUnverifiedStudent = formBlockData?.form_config?.block_unverified_student ?? false;
    const enableMobileOtpVerification = formBlockData?.form_config?.enable_otp_verification ?? false;
    const enableEmailOtpVerification = formBlockData?.form_config?.enable_email_otp_verification ?? false;
    const isMultiVerEnabled = enableMobileOtpVerification && enableEmailOtpVerification;

    const [activeOtpVerf, setActiveOtpVerf] = useState(
        enableMobileOtpVerification ? 'mobile_verf' : enableEmailOtpVerification ? 'email_verf' : 'none',
    );

    const handleChangeVerificationMethod = (type) => {
        if (type === 'email_verf') {
            setActiveOtpVerf(type);
        } else {
            setActiveOtpVerf('mobile_verf');
        }
    };

    // Render primary identifier OTP (auto-OTP for student identifier)
    const renderPrimaryIdentifierOtp = () => {
        if (!isPrimaryIdentifierFlow || !shouldShowPrimaryOtp()) return null;

        const isPrimaryMobile = primaryIdentifier === 'customer_mobile';
        const isPrimaryEmail = primaryIdentifier === 'customer_email';

        if (isPrimaryMobile && enableMobileOtpVerification) {
            // Create a mock mobile input for primary identifier
            const primaryMobileInput = {
                input_id: 'primary_mobile_otp',
                label: 'Verify Mobile Number',
                field_type: 'mobile',
                is_required: true
            };

            return (
                <div className="primary-identifier-otp">
                    <h4 className={classes.OtpHelperText}>Please enter the OTP shared on the entered number</h4>
                    <CustomFormMobile
                        mobileInput={primaryMobileInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={true}
                        formik={formik}
                        isOtpVerificationSuccess={isPrimaryOtpVerified}
                        setIsOtpVerificationSuccess={(value) => {
                            setIsPrimaryOtpVerified(value);
                        }}
                        maskedNumberString={maskedPrimaryString}
                        formBlock={formBlock}
                        formBlockData={formBlockData}
                        isPrimaryIdentifierFlow={true}
                        primaryIdentifierValue={formik?.values?.stud_bd_student_uuid?.trim()}
                    />
                </div>
            );
        }

        if (isPrimaryEmail && enableEmailOtpVerification) {
            // Create a mock email input for primary identifier
            const primaryEmailInput = {
                input_id: 'primary_email_otp',
                label: 'Verify Email Address',
                field_type: 'email',
                is_required: true
            };

            return (
                <div className="primary-identifier-otp">
                    <h4 className={classes.OtpHelperText}>Please enter the OTP shared on the entered email</h4>
                    <CustomFormEmail
                        emailInput={primaryEmailInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={true}
                        formik={formik}
                        isOtpVerificationSuccess={isPrimaryOtpVerified}
                        duplicateMobileNumber={duplicateMobileNumber}
                        setDuplicateMobileNumber={setDuplicateMobileNumber}
                        setIsOtpVerificationSuccess={(value) => {
                            setIsPrimaryOtpVerified(value);
                        }}
                        maskedEmailString={maskedPrimaryString}
                        customerNumber={customerNumber}
                        formBlockData={formBlockData}
                        isPrimaryIdentifierFlow={true}
                        primaryIdentifierValue={formik?.values?.stud_bd_student_uuid?.trim()}
                    />
                </div>
            );
        }

        return null;
    };

    // Handle the complete primary identifier OTP flow
    const renderPrimaryIdentifierOtpFlow = () => {
        const isPrimaryMobile = primaryIdentifier === 'customer_mobile';
        const isPrimaryEmail = primaryIdentifier === 'customer_email';

        // Check if OTP is required for primary identifier
        const primaryOtpRequired = (isPrimaryMobile && enableMobileOtpVerification) || 
                                   (isPrimaryEmail && enableEmailOtpVerification);

        if (primaryOtpRequired) {
            // If primary OTP is enabled, show primary OTP (auto-trigger logic)
            
            if (!isPrimaryOtpVerified) {
                // Show primary OTP verification first
                return renderPrimaryIdentifierOtp();
            } else {
                // Primary OTP is verified, show alternate field without OTP
                if (isPrimaryMobile && emailInput) {
                    return (
                        <CustomFormEmail
                            emailInput={emailInput}
                            studentDetails={studentDetails}
                            isOtpVerificationEnabled={false}
                            formik={formik}
                            isOtpVerificationSuccess={false}
                            setIsOtpVerificationSuccess={setIsOtpVerified}
                            formBlockData={formBlockData}
                            isDisabled={studentDetails?.cust_bd?.email}
                        />
                    );
                }

                if (isPrimaryEmail && mobileInput) {
                    return (
                        <CustomFormMobile
                            mobileInput={mobileInput}
                            studentDetails={studentDetails}
                            isOtpVerificationEnabled={false}
                            formik={formik}
                            isOtpVerificationSuccess={false}
                            setIsOtpVerificationSuccess={setIsOtpVerified}
                            formBlock={formBlock}
                            formBlockData={formBlockData}
                            isDisabled={
                                studentDetails?.customer_mobile &&
                                studentDetails?.customer_mobile?.length === 10 &&
                                !hasDollarInString(studentDetails?.customer_mobile)
                            }
                        />
                    );
                }

                // No alternate field available
                return null;
            }
        } else {
            // If primary OTP is not enabled, check for customer OTP requirement
            let customerOtpRequired = false;
            let customerOtpComponent = null;

            if (isPrimaryMobile && enableEmailOtpVerification && emailInput) {
                // Primary is mobile but no mobile OTP, check email OTP
                customerOtpRequired = true;
                customerOtpComponent = (
                    <CustomFormEmail
                        emailInput={emailInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={enableEmailOtpVerification}
                        formik={formik}
                        isOtpVerificationSuccess={isOtpVerified}
                        setIsOtpVerificationSuccess={setIsOtpVerified}
                        maskedEmailString={maskedEmailString}
                        duplicateMobileNumber={duplicateMobileNumber}
                        setDuplicateMobileNumber={setDuplicateMobileNumber}
                        customerNumber={formik?.values?.stud_bd_student_uuid?.trim()}
                        formBlockData={formBlockData}
                    />
                );
            } else if (isPrimaryEmail && enableMobileOtpVerification && mobileInput) {
                // Primary is email but no email OTP, check mobile OTP
                customerOtpRequired = true;
                customerOtpComponent = (
                    <CustomFormMobile
                        mobileInput={mobileInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={enableMobileOtpVerification}
                        formik={formik}
                        isOtpVerificationSuccess={isOtpVerified}
                        setIsOtpVerificationSuccess={setIsOtpVerified}
                        maskedNumberString={maskedNumberString}
                        formBlock={formBlock}
                        formBlockData={formBlockData}
                    />
                );
            }


            if (customerOtpRequired) {
                // Customer OTP is required, show it
                return customerOtpComponent;
            } else {
                // No OTP required at all, show the alternate field without OTP
                if (isPrimaryMobile && emailInput) {
                    return (
                        <CustomFormEmail
                            emailInput={emailInput}
                            studentDetails={studentDetails}
                            isOtpVerificationEnabled={false}
                            formik={formik}
                            isOtpVerificationSuccess={false}
                            setIsOtpVerificationSuccess={setIsOtpVerified}
                            formBlockData={formBlockData}
                            isDisabled={studentDetails?.cust_bd?.email}
                        />
                    );
                }

                if (isPrimaryEmail && mobileInput) {
                    return (
                        <CustomFormMobile
                            mobileInput={mobileInput}
                            studentDetails={studentDetails}
                            isOtpVerificationEnabled={false}
                            formik={formik}
                            isOtpVerificationSuccess={false}
                            setIsOtpVerificationSuccess={setIsOtpVerified}
                            formBlock={formBlock}
                            formBlockData={formBlockData}
                            isDisabled={
                                studentDetails?.customer_mobile &&
                                studentDetails?.customer_mobile?.length === 10 &&
                                !hasDollarInString(studentDetails?.customer_mobile)
                            }
                        />
                    );
                }

                // No alternate field needed
                return null;
            }
        }

        return null;
    };

    const renderActiveOtpVerfFlow = () => {
        // Handle primary identifier flow first
        if (isPrimaryIdentifierFlow) {
            return renderPrimaryIdentifierOtpFlow();
        }

        // Original flow (unchanged)
        if (mobileInput && activeOtpVerf === 'mobile_verf') {
            return (
                <>
                    <CustomFormMobile
                        mobileInput={mobileInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={enableMobileOtpVerification}
                        formik={formik}
                        isOtpVerificationSuccess={isOtpVerified}
                        setIsOtpVerificationSuccess={setIsOtpVerified}
                        maskedNumberString={maskedNumberString}
                        formBlock={formBlock}
                        formBlockData={formBlockData}
                        isMultiVerEnabled={isMultiVerEnabled}
                        handleChangeVerificationMethod={handleChangeVerificationMethod}
                    />
                    {isOtpVerified && emailInput && (
                        <CustomFormEmail
                            emailInput={emailInput}
                            studentDetails={studentDetails}
                            isOtpVerificationEnabled={activeOtpVerf === 'email_verf' && enableEmailOtpVerification}
                            formik={formik}
                            isOtpVerificationSuccess={activeOtpVerf === 'email_verf' && isOtpVerified}
                            setIsOtpVerificationSuccess={setIsOtpVerified}
                            formBlockData={formBlockData}
                            isDisabled={
                                studentDetails?.cust_bd?.email
                            }
                        />
                    )}
                </>
            );
        } else if (emailInput && activeOtpVerf === 'email_verf') {
            return (
                <>
                    <CustomFormEmail
                        emailInput={emailInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={enableEmailOtpVerification}
                        formik={formik}
                        isOtpVerificationSuccess={isOtpVerified}
                        setIsOtpVerificationSuccess={setIsOtpVerified}
                        isMultiVerEnabled={isMultiVerEnabled}
                        handleChangeVerificationMethod={handleChangeVerificationMethod}
                        maskedEmailString={maskedEmailString}
                        duplicateMobileNumber={duplicateMobileNumber}
                        setDuplicateMobileNumber={setDuplicateMobileNumber}
                        customerNumber={customerNumber}
                        formBlockData={formBlockData}
                    />
                    {isOtpVerified && mobileInput && (
                        <CustomFormMobile
                            mobileInput={mobileInput}
                            studentDetails={studentDetails}
                            isOtpVerificationEnabled={activeOtpVerf === 'mobile_verf' && enableMobileOtpVerification}
                            formik={formik}
                            isOtpVerificationSuccess={activeOtpVerf === 'mobile_verf' && isOtpVerified}
                            setIsOtpVerificationSuccess={setIsOtpVerified}
                            formBlock={formBlock}
                            formBlockData={formBlockData}
                            isDisabled={
                                studentDetails?.customer_mobile &&
                                studentDetails?.customer_mobile?.length === 10 &&
                                !hasDollarInString(studentDetails?.customer_mobile)
                            }
                        />
                    )}
                </>
            );
        } else {
            return (
                <>
                    <CustomFormMobile
                        mobileInput={mobileInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={enableMobileOtpVerification}
                        formik={formik}
                        isOtpVerificationSuccess={isOtpVerified}
                        setIsOtpVerificationSuccess={setIsOtpVerified}
                        formBlock={formBlock}
                        isDisabled={
                            studentDetails?.customer_mobile &&
                            studentDetails?.customer_mobile?.length === 10 &&
                            !hasDollarInString(studentDetails?.customer_mobile)
                        }
                    />
                    <CustomFormEmail
                        emailInput={emailInput}
                        studentDetails={studentDetails}
                        isOtpVerificationEnabled={enableEmailOtpVerification}
                        formik={formik}
                        isOtpVerificationSuccess={isOtpVerified}
                        setIsOtpVerificationSuccess={setIsOtpVerified}
                        isDisabled={
                            studentDetails?.cust_bd?.email
                        }
                    />
                </>
            );
        }
    };

    const renderInactiveStudentUI = () => {
        return (
            <div className={classes.NoStudentDataMessage}>
                <span className={classes.Icon}>
                    <MdLockPerson fontSize="1.25rem" />
                </span>
                <h3 className={classes.NoStudentDataMessage__Title}>Inactive Student</h3>
                <p className={classes.NoStudentDataMessage__Text}>
                    Looks like this student is marked as "Inactive". Please contact your institute regarding this issue
                </p>
            </div>
        );
    };

    const renderStdDataNotAvailableUI = () => {
        return (
            <div className={classes.NoStudentDataMessage}>
                <h3 className={classes.NoStudentDataMessage__Title}>Student Data not available</h3>
                <p className={classes.NoStudentDataMessage__Text}>
                    Please reach out to us at <a href="mailto:support@grayquest.com">support@grayquest.com</a>
                </p>
            </div>
        );
    };

    if (!blockUnverifiedStudent) {
        if (studentDetails) {
            return isActiveStudent ? renderActiveOtpVerfFlow() : renderInactiveStudentUI();
        } else {
            return studentDetailsApiCount > 0 ? renderActiveOtpVerfFlow() : null;
        }
    }

    return (
        <div>
            {isStudentVerified && studentDetails ? (
                <>{isActiveStudent ? renderActiveOtpVerfFlow() : renderInactiveStudentUI()}</>
            ) : (
                studentDetailsApiCount > 0 ? renderStdDataNotAvailableUI() : null
            )}
        </div>
    );
};

CfOtpVerification.propTypes = {
    formBlockData: PropTypes.object,
    formik: PropTypes.object,
    studentDetails: PropTypes.object,
    isStudentVerified: PropTypes.bool,
    studentDetailsApiCount: PropTypes.number,
    isOtpVerified: PropTypes.bool,
    setIsOtpVerified: PropTypes.func,
    formBlock: PropTypes.object,
    maskedNumberString: PropTypes.string,
    maskedEmailString: PropTypes.string,
    duplicateMobileNumber: PropTypes.string,
    setDuplicateMobileNumber: PropTypes.func,
    customerNumber: PropTypes.string,
    // Primary identifier props
    primaryIdentifier: PropTypes.string,
    isPrimaryIdentifierFlow: PropTypes.bool,
    isPrimaryOtpSent: PropTypes.bool,
    setIsPrimaryOtpSent: PropTypes.func,
    isPrimaryOtpVerified: PropTypes.bool,
    setIsPrimaryOtpVerified: PropTypes.func,
    maskedPrimaryString: PropTypes.string,
    shouldShowPrimaryOtp: PropTypes.func,
    shouldShowCustomerOtpWithPrimary: PropTypes.func,
};

export default CfOtpVerification;
